<script setup lang="ts">
import { useElementBounding, useVModel } from '@vueuse/core'

import DropdownVars from './DropdownVars.vue'

import {
  convertBlocksToText,
  convertTextToBlocks,
  countTotalCharacters,
  ensureOneSpaceInsideVars,
  findInVarListFromCode,
  hasEmojiCharacters,
  isInVarList,
  removeDoubleSpacesAroundVars,
  wrapUrlsWithCustomLink
} from './utils'

import type { Survey } from '@/types/types'
import type { CustomVars, FormattedBlock, I18nStrings } from './interfaces'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    modelValue: string
    i18nStrings: I18nStrings
    label?: string
    labelBadge?: string
    customVars: CustomVars[]
    maxLength?: number
    isDisabled?: boolean
    messageContentRef?: HTMLTextAreaElement | null
    dataCy?: string | null
    sanitizeForGsm?: boolean
    forceError?: boolean
    requiredStoreIds?: number[]
    /** When set (with loading/error flags as needed), skips the internal `/surveys` fetch. */
    surveys?: Survey[] | null
    surveysLoading?: boolean
    surveysError?: boolean
  }>(),
  {
    maxLength: null,
    messageContentRef: null,
    dataCy: null,
    sanitizeForGsm: false,
    forceError: false
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'update:exampleValue', value: string): void
  (e: 'update:messageLength', value: number): void
  (e: 'update:messageContentRef', value: HTMLTextAreaElement): void
  (e: 'hasError', value: boolean): void
}>()

const textModelValue = useVModel(props, 'modelValue', emit)
const messageContentRefModel = useVModel(props, 'messageContentRef', emit)

const formattedBlocks = ref<FormattedBlock[]>([])

const isEditing = ref<boolean>(false)
const isClickedFocused = ref<boolean>(false)

const lastCaretPosition = ref<number>(null)

const textareaRef = ref(null)
const editableDiv = ref(null)

const errorUnknownVar = computed(() => {
  let customVarInMessage = formattedBlocks.value.filter((block) => block.isVar) ?? []

  if (
    customVarInMessage?.some((block) => block.textValue?.startsWith('customerApp(survey/')) &&
    props.surveys === null
  ) {
    return false
  }

  return customVarInMessage.some(
    (block) =>
      !isInVarList({
        varCode: block.textValue,
        customVars: props.customVars,
        surveys: props.surveys as Survey[],
        requiredStoreIds: props.requiredStoreIds
      })
  )
})

const errorEmojiInMessage = computed(() => {
  if (props.sanitizeForGsm) {
    return hasEmojiCharacters(textModelValue.value)
  }
  return false
})

const hasError = computed(
  () =>
    errorUnknownVar.value ||
    props.forceError ||
    errorEmojiInMessage.value ||
    (props.maxLength != null &&
      countTotalCharacters({
        textModelValue: textModelValue.value,
        varList: props.customVars,
        sanitizeForGsm: props.sanitizeForGsm
      }) > props.maxLength)
)

let lastEmittedHasError: boolean = false

const emitHasErrorIfChanged = (value: boolean) => {
  if (lastEmittedHasError === value) {
    return
  }

  lastEmittedHasError = value
  emit('hasError', value)
}

const { width, left } = useElementBounding(editableDiv)

const textareaWidth = computed(() => width.value)
const textareaLeftPosition = computed(() => left.value)

const handleUpdateVar = (code: string, index: number) => {
  const customVar = findInVarListFromCode(code, props.customVars)

  if (formattedBlocks.value[index] && customVar?.disabled !== true) {
    formattedBlocks.value[index] = {
      isVar: true,
      textValue: code
    }

    const newFormattedText = convertBlocksToText(formattedBlocks.value)

    refreshEditableSpans(newFormattedText)
  }
}

const handleInsertVar = (code: string) => {
  const customVar = findInVarListFromCode(code, props.customVars)

  if (customVar?.disabled !== true) {
    const insertPosition = lastCaretPosition.value ?? textModelValue.value.length

    const newFormattedText = `${textModelValue.value.slice(
      0,
      insertPosition
    )} {{ ${code} }} ${textModelValue.value.slice(insertPosition)}`

    lastCaretPosition.value = insertPosition + 7 + code.length

    refreshEditableSpans(removeDoubleSpacesAroundVars(newFormattedText))
  }
}

const refreshEditableSpans = (newValue: string) => {
  formattedBlocks.value = convertTextToBlocks(newValue)

  emit('update:modelValue', newValue)
  emit('update:exampleValue', convertBlocksToText(formattedBlocks.value, true, props.customVars))
}

const switchToGUI = () => {
  isEditing.value = false

  const trimmedVarsText = ensureOneSpaceInsideVars(textModelValue.value)
  if (textModelValue.value !== trimmedVarsText) {
    refreshEditableSpans(trimmedVarsText)
  }
}

const switchToTextarea = (isCancelled = false) => {
  if (isCancelled === false) {
    let insertPosition = lastCaretPosition.value ?? textModelValue.value.length

    isEditing.value = true

    nextTick(() => {
      textareaRef.value.focus()
      textareaRef.value.setSelectionRange(insertPosition, insertPosition)
      isClickedFocused.value = false
    })
  }
}

const saveCaretPosition = (event: Event) => {
  lastCaretPosition.value = (event.target as HTMLTextAreaElement).selectionStart

  handleCustomLinks()
}

const openAtClickedPosition = (event: MouseEvent) => {
  if (props.isDisabled) {
    return
  }
  lastCaretPosition.value = getClickedPosition(event)

  switchToTextarea()
}

const getClickedPosition = (event: MouseEvent) => {
  const selection = window.getSelection()

  let clickedPosition = selection.getRangeAt(0).startOffset

  const clickedElement = event.target as HTMLElement
  const nearestBlockSpan = clickedElement.closest('span[data-index]') as HTMLElement

  if (nearestBlockSpan) {
    const blockSpanIndex = parseInt(nearestBlockSpan.getAttribute('data-index') || '0', 10)

    if (blockSpanIndex > 0) {
      for (let i = 0; i < blockSpanIndex; i++) {
        const element = formattedBlocks.value[i]
        const offsetVarText = element.isVar ? 6 : 0 // character count of "{{ " and " }}"
        clickedPosition += element.textValue.length + offsetVarText
      }
    }
  } else {
    clickedPosition = textModelValue.value.length
  }

  return clickedPosition
}

const getVarClasses = computed(() => (item: FormattedBlock) => {
  const found = findInVarListFromCode(item.textValue, props.customVars)

  const baseClasses = props.isDisabled ? 'cursor-default' : 'cursor-pointer'
  const isValidVar = isInVarList({
    varCode: item.textValue,
    customVars: props.customVars,
    surveys: props.surveys as Survey[],
    requiredStoreIds: props.requiredStoreIds
  })
  const validHoverClasses = props.isDisabled ? [] : ['hover:bg-accent']
  const disabledVarHoverClasses = props.isDisabled ? [] : ['hover:bg-yellow-200 hover:border-yellow-300']
  const unkownVarHoverClasses = props.isDisabled ? [] : ['hover:bg-red-200 hover:border-red-300']

  const skipMissingSurveyError = props.surveys === null && item.textValue?.startsWith('customerApp(survey/')

  if ((isValidVar && !found?.disabled) || skipMissingSurveyError) {
    return [baseClasses, 'border-input bg-stone-200', ...validHoverClasses]
  }

  if (isValidVar && found?.disabled) {
    return [baseClasses, 'border-yellow-200 bg-yellow-100', ...disabledVarHoverClasses]
  }

  return [baseClasses, 'border-red-200 bg-red-100', ...unkownVarHoverClasses]
})

const handleCustomLinks = () => {
  const hasCustomLink = props.customVars.some((customVar) => customVar.code === 'customLink')

  let processedText = textModelValue.value
  if (hasCustomLink) {
    processedText = wrapUrlsWithCustomLink(textModelValue.value)
  }

  if (processedText !== textModelValue.value) {
    textModelValue.value = processedText
  }
}

const refreshPreviewText = () => {
  emit('update:exampleValue', convertBlocksToText(formattedBlocks.value, true, props.customVars))
}

const updateBlocks = () => {
  formattedBlocks.value = convertTextToBlocks(textModelValue.value)
  messageContentRefModel.value = textareaRef.value

  emit('update:exampleValue', convertBlocksToText(formattedBlocks.value, true, props.customVars))
  emit(
    'update:messageLength',
    countTotalCharacters({
      textModelValue: textModelValue.value,
      varList: props.customVars,
      sanitizeForGsm: props.sanitizeForGsm
    })
  )
}

onMounted(() => {
  updateBlocks()
})

watch(textModelValue, () => {
  updateBlocks()
})

watch(hasError, emitHasErrorIfChanged, { immediate: true })

defineExpose({
  refreshPreviewText
})
</script>

<template>
  <div class="sm:col-span-6">
    <label :for="label" class="flex items-center gap-1 text-sm text-foreground">
      <span>{{ i18nStrings?.textareaLabel }}</span>
      <Badge v-if="labelBadge" variant="secondary">{{ labelBadge }}</Badge>
    </label>

    <div
      v-if="isEditing === false"
      :id="label"
      ref="editableDiv"
      :name="label"
      :class="[
        'mt-2 min-h-[78px] w-full whitespace-pre-wrap rounded-control border px-3 py-2 border-input sm:text-sm',
        {
          'cursor-default bg-muted text-foreground-disabled !border-input': isDisabled,
          'bg-white text-foreground': !isDisabled,
          '!border-red-300': hasError
        }
      ]"
      tabindex="0"
      @mousedown="isClickedFocused = true"
      @mouseup="openAtClickedPosition($event)"
      @focusin="switchToTextarea(isClickedFocused)"
    >
      <span v-for="(item, index) in formattedBlocks" :key="'text-block-' + index" :data-index="index">
        <span
          v-if="item.isVar"
          :class="['relative inline-block rounded-md border px-1 leading-[18px]', getVarClasses(item)]"
          @mouseup="$event.stopPropagation()"
        >
          <span style="line-height: 0; font-size: 0">{</span>
          <span style="line-height: 0; font-size: 0">{&nbsp;</span>
          <DropdownVars
            type="update"
            :label="item.textValue"
            :custom-vars="customVars"
            :i18n-strings="i18nStrings"
            :surveys="surveys"
            :required-store-ids="requiredStoreIds"
            :is-loading-surveys="surveysLoading"
            :load-surveys-error="surveysError"
            :textarea-position="{ width: textareaWidth, left: textareaLeftPosition }"
            @var-clicked="handleUpdateVar($event, index)"
          />
          <span style="line-height: 0; font-size: 0">&nbsp;}</span>
          <span style="line-height: 0; font-size: 0">}</span>
        </span>

        <span v-else class="inline whitespace-pre-wrap outline-hidden!" v-text="item.textValue"></span>
      </span>
    </div>

    <textarea
      v-show="isEditing === true"
      ref="textareaRef"
      v-model="textModelValue"
      :data-cy="dataCy"
      class="mt-2 block min-h-[78px] w-full resize-none whitespace-pre-wrap rounded-control px-3 py-2 border border-primary !ring-0 sm:text-sm"
      :class="{
        '!border-red-300': hasError
      }"
      @blur="saveCaretPosition($event)"
      @focusout="switchToGUI"
    />

    <div class="flex flex-row items-center">
      <div class="flex flex-1">
        <p v-if="$slots.messageLength" class="mt-2 text-xs text-foreground-secondary">
          <slot name="messageLength" />
        </p>
        <p v-else class="mt-2 text-xs text-foreground-secondary">
          {{
            countTotalCharacters({
              textModelValue: textModelValue,
              varList: customVars,
              sanitizeForGsm: sanitizeForGsm
            })
          }}
          / {{ maxLength }} {{ i18nStrings.characters }}
        </p>
      </div>
      <div v-if="!isDisabled" class="relative">
        <DropdownVars
          v-if="customVars?.length > 0"
          :label="i18nStrings.insertVar"
          :custom-vars="customVars"
          :i18n-strings="i18nStrings"
          :surveys="surveys"
          :required-store-ids="requiredStoreIds"
          :is-loading-surveys="surveysLoading"
          :load-surveys-error="surveysError"
          :textarea-position="{ width: textareaWidth, left: textareaLeftPosition }"
          @var-clicked="handleInsertVar"
        />
      </div>
    </div>

    <ul class="text-xs text-foreground-secondary list-disc list-inside gap-0.5 flex flex-col">
      <li v-if="errorEmojiInMessage" class="text-red-600">
        {{ i18nStrings.errorEmojiInMessage }}
      </li>
      <li v-if="errorUnknownVar" class="text-red-600">
        {{ i18nStrings.errorUnknownVar || t('ui.error.strings.unknownVar') }}
      </li>
    </ul>

    <slot />
  </div>
</template>
